import React, { useEffect, useRef, useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';

interface NumberInputProps {
  id?: string;
  'data-testid'?: string;
  value: number | string;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  className?: string;
}

const normalizeNumberInputValue = (value: number | string, max?: number) => {
  if (typeof value !== 'string' && isNaN(value)) {
    return '';
  }

  if (max !== undefined && typeof value !== 'string' && value > max) {
    return max;
  }

  return value;
};

const getIncrementedValue = (valueInput: number | string, max?: number): number | undefined => {
  const value = typeof valueInput !== 'string' ? valueInput : 0;

  if (max === undefined || value < max) {
    return value + 1;
  }
};

const getDecrementedValue = (valueInput: number | string, min?: number): number | undefined => {
  const value = typeof valueInput !== 'string' ? valueInput : 0;

  if (min === undefined || value > min) {
    return value - 1;
  }
};

const getChangedNumberInputValue = (
  rawValue: string,
  min?: number,
  max?: number,
): { value: number | ''; nextInputValue: number | '' } | undefined => {
  const normalizedRawValue = rawValue.replace(/^0+(?!$)/, '');

  if (normalizedRawValue === '') {
    return { value: '', nextInputValue: '' };
  }

  if (!/^\d+$/.test(normalizedRawValue)) {
    return;
  }

  const value = Number(normalizedRawValue);

  if (max !== undefined && value > max) {
    return;
  }

  if (min !== undefined && value < min) {
    return;
  }

  return { value, nextInputValue: value };
};

const NumberInput = ({
  id,
  'data-testid': dataTestId,
  value,
  min = 0,
  max,
  onChange,
  className = '',
}: NumberInputProps) => {
  value = normalizeNumberInputValue(value, max);

  const [valueInput, setValueInput] = useState(value);
  const lastReportedValueRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (value === lastReportedValueRef.current) {
      lastReportedValueRef.current = undefined;
      return;
    }

    setValueInput(value);
  }, [value]);

  const handleIncrement: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const nextValue = getIncrementedValue(valueInput, max);

    if (nextValue !== undefined) {
      lastReportedValueRef.current = nextValue;
      onChange(nextValue);
      setValueInput(nextValue);
    }
  };

  const handleDecrement: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const nextValue = getDecrementedValue(valueInput, min);

    if (nextValue !== undefined) {
      lastReportedValueRef.current = nextValue;
      onChange(nextValue);
      setValueInput(nextValue);
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextValue = getChangedNumberInputValue(e.target.value, min, max);

    if (nextValue === undefined) {
      return;
    }

    e.target.value = nextValue.nextInputValue.toString();
    if (nextValue.value !== '') {
      lastReportedValueRef.current = nextValue.value;
      onChange(nextValue.value);
    }
    setValueInput(nextValue.nextInputValue);
  };

  return (
    <div className="flex items-center number-input">
      <button
        className="bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-s-lg p-2 h-10 focus:ring-gray-100 focus:ring-2 focus:outline-none"
        onClick={handleDecrement}
      >
        <MinusIcon className="h-4 w-4" />
      </button>
      <input
        id={id}
        data-testid={dataTestId}
        type="number"
        value={valueInput}
        min={min}
        max={max}
        onChange={handleChange}
        className={`${className} input-no-spinner w-16 px-1 py-2 text-center border border-x-0 focus:outline-none focus:ring focus:border-blue-300`}
      />
      <button
        className="bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-e-lg p-2 h-10 focus:ring-gray-100 focus:ring-2 focus:outline-none"
        onClick={handleIncrement}
      >
        <PlusIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default NumberInput;
